/**
 * F007: Lender Match Results Panel — Golden Path Wave 3
 *
 * Displays lender matching results for a deal: card grid of matched products,
 * filter/sort controls, comparison table, and ineligible products section.
 * Calls F004 matching engine endpoints.
 *
 * Spec: specs/golden-path/07-lender-match-results-panel.spec.md
 */

import { useState, useMemo, useEffect, useRef } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Loader2, Search, ChevronDown, ChevronRight, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Slider } from "@/components/ui/slider";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest, ApiError } from "@/lib/queryClient";

// =============================================================================
// Types (mirrors server/services/lender-matching-engine.ts)
// =============================================================================

interface MatchedProduct {
  productId: string;
  lenderId: string;
  lenderName: string;
  productName: string;
  indicativeRate: number | null;
  upfrontRate: number | null;
  trailRate: number | null;
  maxLvr: number | null;
  matchScore: number;
  matchReasons: string[];
}

interface IneligibleProduct {
  productId: string;
  lenderName: string;
  productName: string;
  failReasons: string[];
}

interface MatchResult {
  dealId: string;
  snapshotId: string;
  matchedAt: string | null;
  totalEvaluated: number;
  matches: MatchedProduct[];
  ineligible: IneligibleProduct[];
}

type SortOption = "score" | "rate_asc" | "lvr_desc";

// Named constants — avoids magic literals drifting out of sync
const MAX_RATE_FILTER_PCT = 30;
const MAX_COMPARE_PRODUCTS = 4;

// =============================================================================
// Component
// =============================================================================

interface LenderMatchResultsPanelProps {
  dealId: string;
}

export function LenderMatchResultsPanel({ dealId }: LenderMatchResultsPanelProps) {
  const { toast } = useToast();

  // State
  const [maxRate, setMaxRate] = useState<number>(MAX_RATE_FILTER_PCT);
  const [minLvr, setMinLvr] = useState<number>(0);
  const [showIneligible, setShowIneligible] = useState(false);
  const [sortBy, setSortBy] = useState<SortOption>("score");
  const [comparedIds, setComparedIds] = useState<Set<string>>(new Set());
  const [expandedReasons, setExpandedReasons] = useState<Set<string>>(new Set());
  const [noSnapshotError, setNoSnapshotError] = useState(false);
  const [comparisonOpen, setComparisonOpen] = useState(false);
  const [ineligibleOpen, setIneligibleOpen] = useState(false);

  // EDG-301 F1: tracks whether we've already auto-initialised the toggle for this match run
  const ineligibleInitialisedRef = useRef(false);

  // Fetch stored matches
  const {
    data: matchResult,
    isLoading,
    error: fetchError,
  } = useQuery<MatchResult>({
    queryKey: [`/api/financial/deals/${dealId}/lender-matches`],
    enabled: !!dealId,
    retry: false,
  });

  // Run match mutation
  const runMatchMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", `/api/financial/deals/${dealId}/match-lenders`);
      return (await res.json()) as MatchResult;
    },
    onSuccess: () => {
      setNoSnapshotError(false);
      ineligibleInitialisedRef.current = false; // allow re-init when new match results arrive
      queryClient.invalidateQueries({
        queryKey: [`/api/financial/deals/${dealId}/lender-matches`],
      });
      toast({ title: "Lender matching complete" });
    },
    onError: (error: Error) => {
      if (error instanceof ApiError && error.code === "NO_SNAPSHOT") {
        setNoSnapshotError(true);
      } else {
        toast({
          title: "Matching failed",
          description: error.message,
          variant: "destructive",
        });
      }
    },
  });

  // Derived data
  const hasResults = !!matchResult && !fetchError;
  const dataIntegrityError = hasResults && !Array.isArray(matchResult?.matches);
  const matches = matchResult?.matches ?? [];
  const ineligible = matchResult?.ineligible ?? [];

  // EDG-301 F1: Auto-init "Show ineligible" once per match run — guard prevents
  // re-checking the toggle if user has explicitly unchecked it and a re-fetch
  // returns a different ineligible count.
  useEffect(() => {
    if (!ineligibleInitialisedRef.current && ineligible.length > 0) {
      ineligibleInitialisedRef.current = true;
      setShowIneligible(true);
    }
  }, [ineligible.length]);

  // Filtered + sorted matches
  const filteredMatches = useMemo(() => {
    const filtered = matches.filter((m) => {
      if (m.indicativeRate !== null && m.indicativeRate > maxRate) return false;
      if (m.maxLvr !== null && m.maxLvr < minLvr) return false;
      return true;
    });

    filtered.sort((a, b) => {
      switch (sortBy) {
        case "score":
          return b.matchScore - a.matchScore;
        case "rate_asc":
          return (a.indicativeRate ?? Infinity) - (b.indicativeRate ?? Infinity);
        case "lvr_desc":
          return (b.maxLvr ?? 0) - (a.maxLvr ?? 0);
        default:
          return 0;
      }
    });

    return filtered;
  }, [matches, maxRate, minLvr, sortBy]);

  // Comparison products
  const comparedProducts = useMemo(
    () => matches.filter((m) => comparedIds.has(m.productId)),
    [matches, comparedIds]
  );

  function toggleCompare(productId: string) {
    setComparedIds((prev) => {
      const next = new Set(prev);
      if (next.has(productId)) {
        next.delete(productId);
      } else if (next.size < MAX_COMPARE_PRODUCTS) {
        next.add(productId);
      }
      return next;
    });
  }

  function toggleReasons(productId: string) {
    setExpandedReasons((prev) => {
      const next = new Set(prev);
      if (next.has(productId)) {
        next.delete(productId);
      } else {
        next.add(productId);
      }
      return next;
    });
  }

  function getScoreColor(score: number): string {
    if (score >= 80) return "bg-green-100 text-green-800";
    if (score >= 60) return "bg-amber-100 text-amber-800";
    return "bg-red-100 text-red-800";
  }

  function formatRate(rate: number | null): string {
    if (rate === null) return "N/A";
    return `${rate.toFixed(2)}%`;
  }

  function formatRelativeTime(dateStr: string): string {
    const diff = Date.now() - new Date(dateStr).getTime();
    const minutes = Math.floor(diff / 60000);
    if (minutes < 1) return "just now";
    if (minutes < 60) return `${minutes} minute${minutes === 1 ? "" : "s"} ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;
    const days = Math.floor(hours / 24);
    return `${days} day${days === 1 ? "" : "s"} ago`;
  }

  // =========================================================================
  // Render
  // =========================================================================

  if (isLoading) {
    return (
      <div data-testid="lender-matches-content" className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Empty state — no matches have been run
  if (!hasResults) {
    return (
      <div data-testid="lender-matches-content">
        <div data-testid="lender-matches-empty" className="flex flex-col items-center justify-center py-16 text-center">
          <Search className="h-12 w-12 text-muted-foreground mb-4" />
          <h3 className="text-lg font-semibold mb-1">No lender matches yet</h3>
          <p className="text-sm text-muted-foreground mb-6">
            Run Match to find products that meet this deal&apos;s criteria.
          </p>
          <Button
            data-testid="button-run-match"
            onClick={() => runMatchMutation.mutate()}
            disabled={runMatchMutation.isPending}
          >
            {runMatchMutation.isPending && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" data-testid="run-match-loading" />
            )}
            Run Match
          </Button>
          {noSnapshotError && (
            <p
              data-testid="no-snapshot-message"
              className="mt-4 text-sm text-amber-600"
            >
              Calculate serviceability first (Financials → Recalculate)
            </p>
          )}
        </div>
      </div>
    );
  }

  // Has results
  return (
    <div data-testid="lender-matches-content" className="space-y-4">
      {/* Run Match button + metadata */}
      <div className="flex items-center justify-between">
        <Button
          data-testid="button-run-match"
          variant="outline"
          size="sm"
          onClick={() => runMatchMutation.mutate()}
          disabled={runMatchMutation.isPending}
        >
          {runMatchMutation.isPending && (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" data-testid="run-match-loading" />
          )}
          Run Match
        </Button>

        {/* AC007: Match metadata */}
        <div data-testid="match-metadata" className="text-xs text-muted-foreground space-x-4">
          <span>Last run: {matchResult.matchedAt ? formatRelativeTime(matchResult.matchedAt) : "Unknown"}</span>
          <span>Products evaluated: {matchResult.totalEvaluated}</span>
          <span>Matches found: {matches.length}</span>
        </div>
      </div>

      {noSnapshotError && (
        <p data-testid="no-snapshot-message" className="text-sm text-amber-600">
          Calculate serviceability first (Financials → Recalculate)
        </p>
      )}

      {/* EDG-301 F2: Data integrity warning for malformed API responses */}
      {dataIntegrityError && (
        <div
          data-testid="data-integrity-warning"
          className="flex items-center gap-2 p-3 rounded-lg border border-amber-300 bg-amber-50 text-amber-800 text-sm"
        >
          <AlertTriangle className="h-4 w-4 shrink-0" />
          <span>Match data incomplete — try running match again.</span>
        </div>
      )}

      {/* AC005: Filter controls */}
      {matches.length > 0 && (
        <div data-testid="match-filter-bar" className="flex flex-wrap items-center gap-4 p-3 rounded-lg border bg-muted/30">
          <div className="flex items-center gap-2">
            <label className="text-xs font-medium">Max Rate:</label>
            <div className="w-32">
              <Slider
                data-testid="filter-max-rate"
                value={[maxRate]}
                onValueChange={([v]) => setMaxRate(v)}
                max={MAX_RATE_FILTER_PCT}
                min={0}
                step={0.25}
              />
            </div>
            <span className="text-xs text-muted-foreground w-12">{formatRate(maxRate)}</span>
          </div>

          <div className="flex items-center gap-2">
            <label className="text-xs font-medium">Min LVR:</label>
            <Input
              data-testid="filter-min-lvr"
              type="number"
              value={minLvr}
              onChange={(e) => setMinLvr(Number(e.target.value))}
              className="w-20 h-8 text-xs"
              min={0}
              max={100}
            />
          </div>

          <div className="flex items-center gap-2">
            <Checkbox
              data-testid="filter-show-ineligible"
              checked={showIneligible}
              onCheckedChange={(checked) => setShowIneligible(!!checked)}
            />
            <label className="text-xs font-medium">Show ineligible</label>
          </div>

          <div className="flex items-center gap-2 ml-auto">
            <label className="text-xs font-medium">Sort by:</label>
            <Select
              value={sortBy}
              onValueChange={(v) => setSortBy(v as SortOption)}
            >
              <SelectTrigger data-testid="filter-sort-by" className="w-40 h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="score">Match Score</SelectItem>
                <SelectItem value="rate_asc">Rate (low to high)</SelectItem>
                <SelectItem value="lvr_desc">LVR (high to low)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      )}

      {/* AC004: Match results card grid */}
      {filteredMatches.length > 0 ? (
        <div data-testid="match-card-grid" className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {filteredMatches.map((product) => (
            <Card key={product.productId} data-testid={`match-card-${product.productId}`}>
              <CardContent className="pt-4">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <p data-testid="card-lender-name" className="font-semibold text-sm">
                      {product.lenderName}
                    </p>
                    <p className="text-xs text-muted-foreground">{product.productName}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge
                      data-testid="card-match-score"
                      className={getScoreColor(product.matchScore)}
                    >
                      {product.matchScore}
                    </Badge>
                    <Checkbox
                      data-testid={`compare-checkbox-${product.productId}`}
                      checked={comparedIds.has(product.productId)}
                      onCheckedChange={() => toggleCompare(product.productId)}
                      disabled={!comparedIds.has(product.productId) && comparedIds.size >= MAX_COMPARE_PRODUCTS}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                  <div>
                    <span className="text-muted-foreground">Indicative rate: </span>
                    <span className="font-medium">
                      {product.indicativeRate !== null ? `${product.indicativeRate.toFixed(2)}% p.a.` : "N/A"}
                    </span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Max LVR: </span>
                    <span className="font-medium">
                      {product.maxLvr !== null ? `${product.maxLvr}%` : "N/A"}
                    </span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Upfront: </span>
                    <span className="font-medium">{formatRate(product.upfrontRate)}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Trail: </span>
                    <span className="font-medium">{formatRate(product.trailRate)}</span>
                  </div>
                </div>

                {/* Match reasons — expandable */}
                {product.matchReasons.length > 0 && (
                  <button
                    className="flex items-center gap-1 mt-2 text-xs text-muted-foreground hover:text-foreground"
                    onClick={() => toggleReasons(product.productId)}
                  >
                    {expandedReasons.has(product.productId) ? (
                      <ChevronDown className="h-3 w-3" />
                    ) : (
                      <ChevronRight className="h-3 w-3" />
                    )}
                    Match reasons ({product.matchReasons.length})
                  </button>
                )}
                {expandedReasons.has(product.productId) && (
                  <ul className="mt-1 text-xs text-muted-foreground list-disc list-inside">
                    {product.matchReasons.map((reason) => (
                      <li key={reason}>{reason}</li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div data-testid="no-eligible-matches" className="text-center py-8 text-sm text-muted-foreground">
          No eligible products match the current filters.
        </div>
      )}

      {/* AC006: Comparison table */}
      {comparedProducts.length > 0 && (
        <Collapsible
          data-testid="comparison-section"
          open={comparisonOpen}
          onOpenChange={setComparisonOpen}
        >
          <CollapsibleTrigger
            data-testid="comparison-trigger"
            className="flex items-center gap-2 text-sm font-medium hover:text-primary"
          >
            {comparisonOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            Compare Products ({comparedProducts.length})
          </CollapsibleTrigger>
          <CollapsibleContent className="mt-2">
            <div className="overflow-x-auto border rounded-lg">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="sticky left-0 bg-background">Field</TableHead>
                    {comparedProducts.map((p) => (
                      <TableHead key={p.productId}>{p.lenderName}</TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <TableRow>
                    <TableCell className="sticky left-0 bg-background font-medium">Product</TableCell>
                    {comparedProducts.map((p) => (
                      <TableCell key={p.productId}>{p.productName}</TableCell>
                    ))}
                  </TableRow>
                  <TableRow>
                    <TableCell className="sticky left-0 bg-background font-medium">Rate</TableCell>
                    {comparedProducts.map((p) => (
                      <TableCell key={p.productId}>
                        {p.indicativeRate !== null ? `${p.indicativeRate.toFixed(2)}% p.a.` : "N/A"}
                      </TableCell>
                    ))}
                  </TableRow>
                  <TableRow>
                    <TableCell className="sticky left-0 bg-background font-medium">Max LVR</TableCell>
                    {comparedProducts.map((p) => (
                      <TableCell key={p.productId}>
                        {p.maxLvr !== null ? `${p.maxLvr}%` : "N/A"}
                      </TableCell>
                    ))}
                  </TableRow>
                  <TableRow>
                    <TableCell className="sticky left-0 bg-background font-medium">Match Score</TableCell>
                    {comparedProducts.map((p) => (
                      <TableCell key={p.productId}>{p.matchScore}</TableCell>
                    ))}
                  </TableRow>
                  <TableRow>
                    <TableCell className="sticky left-0 bg-background font-medium">Upfront</TableCell>
                    {comparedProducts.map((p) => (
                      <TableCell key={p.productId}>{formatRate(p.upfrontRate)}</TableCell>
                    ))}
                  </TableRow>
                  <TableRow>
                    <TableCell className="sticky left-0 bg-background font-medium">Trail</TableCell>
                    {comparedProducts.map((p) => (
                      <TableCell key={p.productId}>{formatRate(p.trailRate)}</TableCell>
                    ))}
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          </CollapsibleContent>
        </Collapsible>
      )}

      {/* AC008: Ineligible products section (EDG-301 F1: toggle controls visibility) */}
      {showIneligible && (
        <Collapsible
          data-testid="ineligible-section"
          open={ineligibleOpen}
          onOpenChange={setIneligibleOpen}
        >
          <CollapsibleTrigger
            data-testid="ineligible-trigger"
            className="flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground"
          >
            {ineligibleOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            Ineligible Products ({ineligible.length})
          </CollapsibleTrigger>
          <CollapsibleContent data-testid="ineligible-content" className="mt-2">
            {ineligible.length === 0 ? (
              <p className="text-xs text-muted-foreground py-2">No ineligible products.</p>
            ) : (
              <div className="space-y-2">
                {ineligible.map((product) => (
                  <div
                    key={product.productId}
                    className="flex items-start justify-between p-2 rounded border"
                  >
                    <div>
                      <p className="text-sm font-medium">{product.lenderName}</p>
                      <p className="text-xs text-muted-foreground">{product.productName}</p>
                    </div>
                    <div className="text-right">
                      <Badge variant="destructive" className="text-xs">Ineligible</Badge>
                      <ul className="mt-1 text-xs text-muted-foreground">
                        {product.failReasons.map((reason) => (
                          <li key={reason}>{reason}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CollapsibleContent>
        </Collapsible>
      )}
    </div>
  );
}
